import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { currentSession } from '@/api/client'
import { allerALaPorte, annoncerPorteInjoignable } from '@/identite/porte'
import { peutUtiliserSuperuser } from '@/navigation'

/**
 * Routes de l'application.
 *
 * Chaque vue est chargée PARESSEUSEMENT (`() => import(...)`) : Vite en fait un
 * fragment séparé, et l'écran de connexion — le seul que voit un visiteur non
 * authentifié — ne télécharge pas le tableau de bord, ses graphiques et ses
 * dépendances.
 *
 * `meta.public` marque les routes accessibles sans session. Tout le reste exige
 * une session : la liste blanche est plus sûre que la liste noire, où un écran
 * ajouté sans `requiresAuth` devient accessible à tous sans qu'on le remarque.
 */
const routes: RouteRecordRaw[] = [
  // Trois écrans PUBLICS d'accès au compte : acceptation d'invitation, demande
  // et réinitialisation de mot de passe. Leurs chemins sont stables par
  // construction — ils sont écrits dans les courriels envoyés (`core/mail/
  // templates.ts`) : renommer l'un d'eux sans toucher le gabarit, et tous les
  // liens déjà partis mènent au 404 de l'attrape-tout.
  {
    path: '/invitations/accept',
    name: 'invitation.accept',
    component: () => import('@/views/InvitationAcceptView.vue'),
    meta: { public: true },
  },
  // Documentation d'intégration PUBLIQUE : un intégrateur sans compte doit
  // pouvoir la lire avant de décider de s'inscrire. L'onglet actif est porté
  // par l'URL — un lien partagé retombe sur le bon document.
  //
  // `/api` ne dépend QUE du frontend : l'API REST, elle, vit sous `/api/` —
  // le slash final les distingue, et le serveur ne répond que sur des chemins
  // déclarés. Si un jour l'hébergement sert le SPA et l'API derrière le même
  // nom de domaine avec un préfixe, c'est là qu'il faudra déplacer l'API.
  {
    path: '/api',
    name: 'doc.api',
    component: () => import('@/views/DocPubliqueView.vue'),
    meta: { public: true },
  },
  {
    path: '/mcp',
    name: 'doc.mcp',
    component: () => import('@/views/DocPubliqueView.vue'),
    meta: { public: true },
  },
  {
    // Écran PUBLIC du signataire (E6-6) : le jeton vit dans le CHEMIN seul —
    // jamais de query (journaux d'accès, historique). La route porte
    // `meta.public` ; l'écran ne monte aucun élément de session, hors
    // AppLayout — le signataire n'a pas de compte.
    path: '/s/:jeton',
    name: 'signataire.parcours',
    component: () => import('@/views/SignataireParcoursView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      // L'accueil est la LISTE DES ENVELOPPES : le tableau de bord a été retiré
      // du produit, et laisser une redirection vers un écran absent donnerait
      // un 404 sur la page d'entrée.
      { path: '', redirect: { name: 'console.enveloppe.liste' } },
      {
        // Liste paginée des enveloppes (E8-2). AUCUN garde de rôle : la lecture
        // console est ouverte à tout membre authentifié (règle serveur
        // `canReadOrganisationData`, E8-2) — le cloisonnement vient du périmètre
        // organisation, pas du rôle. La garde `meta.roles` afficherait un écran
        // que le serveur laisserait remplir : pire que rien.
        path: 'console/enveloppes',
        name: 'console.enveloppe.liste',
        component: () => import('@/views/ConsoleEnveloppesListeView.vue'),
      },
      {
        // Envoi d'enveloppe (E8-1). AUCUN garde de rôle : la route serveur
        // n'exige que la session (`app.authenticate`) — lui imposer un rôle ici
        // fermerait un geste que le serveur ouvre, sans règle `rules.ts` qui le
        // justifie. Si une habilitation de création apparaît un jour, elle se
        // posera des DEUX côtés dans le même lot.
        path: 'console/enveloppes/nouvelle',
        name: 'console.enveloppe.nouvelle',
        component: () => import('@/views/ConsoleEnvoiView.vue'),
      },
      {
        // Détail « suivi » d'une enveloppe — cible stable de la redirection
        // d'envoi (E8-1) ; les actions arriveront avec E8-3.
        path: 'console/enveloppes/:id',
        name: 'console.enveloppe.detail',
        component: () => import('@/views/ConsoleEnveloppeDetailView.vue'),
      },
      {
        // Modèles de positions de champs (E8-5). AUCUN garde de rôle : la
        // lecture du catalogue est la lecture console ordinaire (quatre rôles —
        // le viewer en a besoin au pré-remplissage de l'envoi), et les écritures
        // sont refusées par le SERVEUR (`peutGererModelesPositions`). Mettre un
        // rôle ici désynchroniserait l'affichage de l'autorité.
        path: 'console/modeles',
        name: 'console.modeles',
        component: () => import('@/views/ConsoleModelesView.vue'),
      },
      {
        // Réglages de signature de l'organisation (E8-6). AUCUN garde de rôle :
        // la lecture est ouverte aux quatre rôles (le pré-remplissage de
        // l'envoi en a besoin), et l'écriture est refusée par le SERVEUR
        // (`peutGererReglagesSignature`). Un rôle ici désynchroniserait
        // l'affichage de l'autorité — même doctrine que les modèles.
        path: 'console/reglages',
        name: 'console.reglages',
        component: () => import('@/views/ConsoleReglagesView.vue'),
      },
      {
        // Solde et journal des crédits (E13-3). AUCUN garde de rôle : la
        // lecture est ouverte aux quatre rôles (`canReadOrganisationData`), et
        // la bascule illimité est refusée par le SERVEUR (`peutReglerIllimite`,
        // owner SEUL). Un rôle ici désynchroniserait l'affichage de l'autorité
        // — même doctrine que les modèles et les réglages.
        path: 'console/credits',
        name: 'console.credits',
        component: () => import('@/views/ConsoleCreditsView.vue'),
      },
      {
        // Console d'horodatage (E14-2) : liste cursée des lots et pièces.
        // AUCUN garde de rôle : la lecture console est ouverte aux quatre rôles
        // (`canReadOrganisationData` côté serveur) — le cloisonnement vient du
        // périmètre organisation, pas du rôle. Même doctrine que la liste
        // enveloppes (E8-2).
        path: 'console/horodatages',
        name: 'console.horodatage.liste',
        component: () => import('@/views/ConsoleHorodatagesListeView.vue'),
      },
      {
        // Détail d'UNE pièce d'horodatage (E14-2) — empreinte, références du
        // jeton, contexte déclaré ; le renommage (seule écriture) y vit, borné
        // côté serveur pour le viewer (403) : aucun garde de rôle ici non plus.
        path: 'console/horodatages/pieces/:pieceId',
        name: 'console.horodatage.piece',
        component: () => import('@/views/ConsoleHorodatagePieceView.vue'),
      },
      {
        // Coupure d'accès signature (E9-7) : destination du rappel
        // `setAccesRetireHandler` (main.ts), pas un lien de menu — nulle part
        // où cliquer pour y aller, on y est ENVOYÉ par le 403 typé.
        path: 'console/acces-retire',
        name: 'console.acces-retire',
        component: () => import('@/views/AccesRetireView.vue'),
      },
      {
        // Le périmètre plateforme a son propre préfixe : l'URL dit dans quel
        // périmètre on se trouve, ce qui compte quand on colle un lien dans un
        // ticket.
        path: 'superuser/organisations',
        name: 'superuser.organisations',
        component: () => import('@/views/SuperuserOrganisationsView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        path: 'superuser/plans',
        name: 'superuser.plans',
        component: () => import('@/views/SuperuserPlansView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        path: 'superuser/services',
        name: 'superuser.services',
        component: () => import('@/views/SuperuserServicesView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        // E12-1 : l'etat du SERVICE lui-meme (configuration et incidents), a
        // distinguer de `superuser/services` qui gere les CONNEXIONS externes
        // enregistrees en base. Ici, rien ne se regle : on constate.
        path: 'superuser/service',
        name: 'superuser.service-etat',
        component: () => import('@/views/SuperuserServiceEtatView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        // En dernier des enfants : la documentation est un recours, là où on
        // la cherche quand le reste n'a pas suffi.
        path: 'developpeurs',
        name: 'developpeurs',
        component: () => import('@/views/DevelopersView.vue'),
      },
    ],
  },
  {
    // Attrape-tout : une URL inconnue montre un écran explicite plutôt qu'une
    // page blanche, qui est indiscernable d'un plantage du bundle.
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // Retour en haut de page à chaque navigation, sauf quand le navigateur
  // restitue une position (bouton Précédent) : on respecte alors son choix.
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

/**
 * Garde d'accès. Elle lit la SESSION PERSISTÉE et non le store Pinia : la garde
 * s'exécute avant le premier montage de composant, moment où le store peut ne
 * pas être hydraté. Le store reste la source de vérité pour l'affichage.
 */
router.beforeEach((to) => {
  const authenticated = currentSession() !== null
  const session = currentSession()

  if (!to.meta.public && !authenticated) {
    /*
     * À LA PORTE, et non vers un écran d'ici : cette application n'en a plus.
     *
     * Elle a eu une route `/login` qui, sur une instance raccordée, ne faisait
     * qu'appeler `allerALaPorte()` au montage — une page de transit qui
     * s'affichait le temps d'un battement et laissait croire que Sign a sa
     * propre connexion. Le trousseau vit chez `account.betool.fr` ; c'est lui
     * qui sait quels comptes sont déjà ouverts.
     *
     * On ANNULE la navigation (`false`) : la page part de toute façon, et
     * laisser le routeur peindre une destination qu'on quitte ferait clignoter
     * un écran vide.
     */
    if (!allerALaPorte()) annoncerPorteInjoignable()
    return false
  }

  // --- Habilitations d'ÉCRAN -------------------------------------------------
  //
  // Le registre de navigation cache les entrées interdites ; cette garde refuse
  // l'ENTRÉE. Les deux sont nécessaires et ne font pas le même travail : un lien
  // profond, un signet ou une URL tapée à la main n'passent pas par le menu. Le
  // serveur reste l'autorité — ces contrôles évitent d'afficher un écran qui se
  // remplirait d'erreurs 403, ils ne protègent aucune donnée.
  const utilisateur = session?.user ?? null

  if (to.meta.scope === 'superuser' && !peutUtiliserSuperuser(utilisateur)) {
    return { name: 'console.enveloppe.liste' }
  }

  const rolesRequis = to.meta.roles as readonly string[] | undefined
  if (rolesRequis !== undefined && !rolesRequis.includes(utilisateur?.role ?? '')) {
    return { name: 'console.enveloppe.liste' }
  }

  return true
})
